import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { Plus, Edit, Trash2, Star, X, Quote } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminTestimonials = () => {
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    role: '',
    quote: '',
    publication_id: '',
    featured: true
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || user.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/admin/login');
      return;
    }
    
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    
    try {
      const [testimonialsRes, publicationsRes] = await Promise.all([
        axios.get(`${API}/admin/testimonials`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/publications`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setTestimonials(testimonialsRes.data);
      setPublications(publicationsRes.data.publications || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingId(null);
    setFormData({
      name: '',
      company: '',
      role: '',
      quote: '',
      publication_id: '',
      featured: true
    });
    setShowModal(true);
  };

  const openEditModal = (testimonial) => {
    setEditingId(testimonial.id);
    setFormData({
      name: testimonial.name,
      company: testimonial.company,
      role: testimonial.role,
      quote: testimonial.quote,
      publication_id: testimonial.publication_id || '',
      featured: testimonial.featured
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    if (!formData.name || !formData.company || !formData.quote) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      if (editingId) {
        await axios.put(`${API}/admin/testimonials/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Testimonial updated');
      } else {
        await axios.post(`${API}/admin/testimonials`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Testimonial created');
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      console.error('Error saving testimonial:', error);
      toast.error('Failed to save testimonial');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    
    const token = localStorage.getItem('token');
    
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Testimonial deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      toast.error('Failed to delete testimonial');
    }
  };

  const toggleFeatured = async (testimonial) => {
    const token = localStorage.getItem('token');
    
    try {
      await axios.put(`${API}/admin/testimonials/${testimonial.id}`, 
        { featured: !testimonial.featured },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchData();
    } catch (error) {
      console.error('Error updating testimonial:', error);
    }
  };

  return (
    <AdminLayout>
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="font-playfair text-4xl font-bold mb-2">Testimonials</h1>
          <p className="font-manrope text-sm text-gray-600">Manage customer testimonials for homepage</p>
        </div>
        <button
          onClick={openCreateModal}
          data-testid="add-testimonial-btn"
          className="flex items-center gap-2 px-6 py-3 bg-black text-white font-manrope text-xs uppercase tracking-widest hover:bg-gray-800 transition-all duration-300"
        >
          <Plus className="w-4 h-4" />
          Add Testimonial
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <p className="font-manrope text-gray-600">Loading...</p>
        </div>
      ) : testimonials.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-gray-300">
          <Quote className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="font-manrope text-gray-600 mb-4">No testimonials yet</p>
          <button onClick={openCreateModal} className="font-manrope text-sm underline">
            Add your first testimonial
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {testimonials.map((testimonial) => (
            <div 
              key={testimonial.id} 
              className={`border p-6 ${testimonial.featured ? 'border-black' : 'border-gray-200'}`}
              data-testid={`testimonial-${testimonial.id}`}
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-manrope font-semibold">{testimonial.name}</h3>
                  <p className="font-manrope text-sm text-gray-600">{testimonial.role}, {testimonial.company}</p>
                  {testimonial.publication_name && (
                    <p className="font-manrope text-xs text-gray-500 mt-1">via {testimonial.publication_name}</p>
                  )}
                </div>
                <button
                  onClick={() => toggleFeatured(testimonial)}
                  className={`p-2 ${testimonial.featured ? 'text-yellow-500' : 'text-gray-300'}`}
                  title={testimonial.featured ? 'Featured' : 'Not featured'}
                >
                  <Star className="w-5 h-5" fill={testimonial.featured ? 'currentColor' : 'none'} />
                </button>
              </div>
              
              <p className="font-manrope text-sm text-gray-700 italic mb-4">"{testimonial.quote}"</p>
              
              <div className="flex gap-2">
                <button
                  onClick={() => openEditModal(testimonial)}
                  className="p-2 border border-gray-200 hover:border-black transition-all"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(testimonial.id)}
                  className="p-2 border border-gray-200 hover:border-red-500 hover:text-red-500 transition-all"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="font-playfair text-xl font-bold">
                {editingId ? 'Edit Testimonial' : 'Add Testimonial'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="John Doe"
                  className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Company *</label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    placeholder="Acme Inc"
                    className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
                  />
                </div>
                <div>
                  <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Role</label>
                  <input
                    type="text"
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                    placeholder="CEO"
                    className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
                  />
                </div>
              </div>
              
              <div>
                <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Quote *</label>
                <textarea
                  value={formData.quote}
                  onChange={(e) => setFormData({...formData, quote: e.target.value})}
                  rows="3"
                  placeholder="Their testimonial..."
                  className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
                />
              </div>
              
              <div>
                <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Publication (Optional)</label>
                <select
                  value={formData.publication_id}
                  onChange={(e) => setFormData({...formData, publication_id: e.target.value})}
                  className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
                >
                  <option value="">Select publication...</option>
                  {publications.map(pub => (
                    <option key={pub.id} value={pub.id}>{pub.name}</option>
                  ))}
                </select>
              </div>
              
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={(e) => setFormData({...formData, featured: e.target.checked})}
                  className="w-4 h-4"
                />
                <span className="font-manrope text-sm">Featured on homepage</span>
              </label>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-6 py-3 border border-gray-200 font-manrope text-xs uppercase tracking-widest hover:border-black transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-black text-white font-manrope text-xs uppercase tracking-widest hover:bg-gray-800 transition-all"
                >
                  {editingId ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminTestimonials;
